/**
 * Which country a place is in, coarsely: enough to dress the police in the
 * right livery, from a handful of built-in outlines rather than any data
 * source.
 *
 * Each country is one or more rough polygons in longitude and latitude,
 * tens of points each. They follow the land borders closely enough to put
 * towns on the right side of them (Strömstad and Halden, Malmö and
 * Copenhagen), and the coasts loosely: a point at sea belongs to whichever
 * outline happens to hold it. Neighbours share their border points, so
 * there is no gap or overlap along a land border. Anywhere else is
 * undefined, and gets a neutral livery.
 */

export type Country = 'se' | 'no' | 'dk' | 'fi' | 'de' | 'uk' | 'us';

type Ring = ReadonlyArray<readonly [number, number]>;

// --- shared land borders, [lon, lat] ------------------------------------------

/** Norway and Sweden, from Idefjorden by Svinesund north to Treriksröset. */
const NO_SE: Ring = [
  [11.12, 59.03], [11.45, 58.99], [11.68, 59.4], [11.82, 59.85], [12.5, 60.4], [12.3, 61.3], [12.25, 62.1],
  [12.05, 63.2], [12.15, 63.6], [13.2, 64.1], [14.15, 64.5], [14.5, 65.3], [15.6, 66.2], [16.5, 67.0],
  [18.0, 68.2], [19.9, 68.36], [20.55, 69.06],
];
/** Sweden and Finland, from Treriksröset down the Torne to its mouth. */
const SE_FI: Ring = [
  [20.55, 69.06], [21.0, 69.0], [22.4, 68.5], [23.4, 68.0], [23.9, 67.3], [23.65, 66.4], [24.14, 65.75],
];
/** Norway and Finland, from Treriksröset east to the Russian border. */
const NO_FI: Ring = [
  [20.55, 69.06], [21.6, 69.3], [22.4, 68.75], [23.5, 68.8], [24.9, 68.6], [25.8, 69.4], [27.0, 69.9], [28.93, 69.05],
];

const reverse = (r: Ring): Ring => [...r].reverse();

// --- the outlines ---------------------------------------------------------------

const SWEDEN: Ring = [
  ...NO_SE,
  ...SE_FI.slice(1),
  // Down the Gulf of Bothnia, round Stockholm's archipelago, Gotland and Öland, to Skåne.
  [22.2, 65.4], [21.6, 64.6], [20.8, 63.8], [19.6, 63.3], [18.4, 62.6], [17.9, 61.6], [17.6, 60.8],
  [18.9, 60.4], [19.4, 59.8], [19.2, 59.2], [18.6, 58.6], [19.4, 57.9], [18.9, 57.1], [17.2, 56.2],
  [15.9, 55.95], [14.4, 55.5], [13.3, 55.25],
  // Up the Öresund and the west coast, Copenhagen across the water.
  [12.88, 55.45], [12.9, 55.75], [12.7, 56.05], [12.4, 56.3], [12.6, 56.6], [11.8, 57.5], [11.6, 57.75],
  [11.15, 58.4], [11.0, 58.9],
];

const NORWAY: Ring = [
  ...reverse(NO_SE),
  ...NO_FI.slice(1),
  // The Russian border, then west and south along the coast to the Oslofjord.
  [30.8, 69.8], [30.0, 70.3], [28.5, 71.2], [25.8, 71.3], [22.0, 70.8], [18.9, 70.1], [16.0, 69.4],
  [12.8, 68.2], [13.5, 67.2], [12.0, 65.8], [10.5, 64.6], [8.6, 63.8], [7.2, 63.2], [5.6, 62.4],
  [4.7, 61.4], [4.8, 60.2], [5.2, 59.1], [5.5, 58.4], [7.0, 57.9], [8.2, 58.0], [9.5, 58.7], [10.5, 58.9],
];

const FINLAND: Ring = [
  ...NO_FI,
  // The Russian border south to the Gulf of Finland.
  [28.6, 68.1], [30.0, 67.6], [29.1, 66.9], [29.9, 66.0], [30.1, 64.7], [30.6, 64.0], [31.6, 62.9],
  [29.3, 61.3], [27.8, 60.55],
  // West along the south coast, Åland, and up the west coast to Tornio.
  [26.0, 60.2], [24.0, 59.9], [22.9, 59.75], [21.3, 59.7], [19.3, 59.8], [19.4, 60.6], [21.2, 61.0],
  [21.0, 62.4], [21.3, 63.4], [23.0, 64.2], [24.5, 64.8], [25.3, 65.2], [24.6, 65.6],
  ...reverse(SE_FI).slice(0, 1),
];

/** Jutland and the islands, the German border just north of Flensburg; not Bornholm. */
const DENMARK: Ring = [
  [8.6, 54.92], [9.4, 54.82], [9.9, 54.8], [10.9, 54.6], [11.5, 54.5], [12.1, 54.5], [12.6, 54.9],
  [12.6, 55.4], [12.7, 55.6], [12.65, 56.05], [12.2, 56.15], [11.0, 56.6], [10.7, 57.7], [10.4, 57.8],
  [9.5, 57.3], [8.2, 57.1], [8.0, 56.0], [8.0, 55.4], [8.4, 55.0],
];

const GERMANY: Ring = [
  // The Danish border, the Baltic coast, Poland down the Oder and Neisse.
  [8.4, 55.0], [8.6, 54.92], [9.4, 54.82], [9.9, 54.8], [10.6, 54.45], [11.2, 54.1], [12.3, 54.25],
  [13.4, 54.75], [14.3, 53.95], [14.4, 53.3], [14.6, 52.6], [14.7, 52.0], [15.0, 51.1],
  // The Czech and Austrian borders.
  [14.3, 50.9], [13.4, 50.6], [12.1, 50.3], [12.5, 49.8], [13.0, 49.3], [13.8, 48.8], [13.5, 48.5],
  [12.9, 47.9], [13.0, 47.5], [12.2, 47.6], [11.0, 47.4], [10.4, 47.3], [9.6, 47.5],
  // Switzerland along the Rhine, France, Luxembourg, Belgium, the Netherlands.
  [8.6, 47.65], [7.6, 47.55], [7.6, 48.3], [8.2, 48.97], [7.0, 49.15], [6.4, 49.45], [6.1, 50.1],
  [6.0, 50.8], [6.1, 51.25], [6.0, 51.8], [6.8, 51.95], [7.05, 52.4], [7.2, 53.2], [7.0, 53.65],
  [8.0, 53.85], [8.9, 54.2], [8.6, 54.6],
];

const GREAT_BRITAIN: Ring = [
  [-5.75, 50.0], [-4.0, 50.3], [-3.0, 50.6], [-1.0, 50.7], [0.6, 50.8], [1.5, 51.15], [1.5, 51.4],
  [1.8, 52.5], [1.3, 53.0], [0.3, 53.6], [-0.4, 54.5], [-1.5, 55.6], [-2.0, 56.0], [-2.6, 56.4],
  [-1.7, 57.5], [-3.0, 58.7], [-5.0, 58.7], [-6.3, 58.5], [-7.7, 57.6], [-6.4, 56.3], [-5.8, 55.3],
  [-5.1, 54.75], [-3.5, 54.9], [-3.6, 54.3], [-3.0, 53.8], [-3.2, 53.3], [-4.7, 53.4], [-4.2, 52.8],
  [-5.4, 51.9], [-4.1, 51.55], [-3.3, 51.4], [-4.3, 51.2],
];

const NORTHERN_IRELAND: Ring = [
  [-8.2, 54.45], [-7.3, 55.3], [-6.0, 55.3], [-5.4, 54.5], [-6.2, 54.0], [-7.1, 54.25],
];

const US_MAINLAND: Ring = [
  // The Canadian border, the 49th parallel, the lakes and the St Lawrence.
  [-124.8, 48.4], [-123.0, 49.0], [-95.2, 49.0], [-89.6, 48.0], [-84.5, 46.5], [-82.4, 43.0],
  [-79.0, 43.4], [-76.5, 44.2], [-75.0, 45.0], [-71.5, 45.0], [-70.0, 46.7], [-69.2, 47.45],
  [-67.8, 47.1], [-67.0, 45.0],
  // The Atlantic and the Gulf.
  [-70.0, 43.7], [-69.9, 41.7], [-74.0, 40.4], [-75.5, 38.5], [-75.5, 35.2], [-79.0, 33.3],
  [-81.0, 31.5], [-80.0, 26.0], [-80.3, 25.0], [-81.8, 24.9], [-82.8, 27.5], [-84.0, 30.0],
  [-88.5, 30.2], [-89.5, 28.9], [-94.0, 29.5], [-97.2, 25.9],
  // Mexico, along the Rio Grande and the line west.
  [-99.5, 27.5], [-101.0, 29.7], [-103.0, 29.0], [-104.5, 29.6], [-106.5, 31.8], [-108.2, 31.3],
  [-111.0, 31.3], [-114.8, 32.5], [-117.1, 32.5],
  // The Pacific.
  [-118.5, 33.8], [-120.6, 34.5], [-122.6, 37.5], [-124.4, 40.4], [-124.2, 46.3],
];

const ALASKA: Ring = [
  [-141.0, 69.7], [-141.0, 60.3], [-137.5, 59.0], [-133.4, 58.4], [-130.0, 55.9], [-130.0, 54.6],
  [-134.0, 54.6], [-150.0, 58.8], [-165.0, 54.3], [-168.0, 65.6], [-156.6, 71.5],
];

const HAWAII: Ring = [[-160.6, 18.8], [-154.6, 18.8], [-154.6, 22.4], [-160.6, 22.4]];

const OUTLINES: ReadonlyArray<readonly [Country, Ring]> = [
  ['se', SWEDEN], ['no', NORWAY], ['fi', FINLAND], ['dk', DENMARK], ['de', GERMANY],
  ['uk', GREAT_BRITAIN], ['uk', NORTHERN_IRELAND], ['us', US_MAINLAND], ['us', ALASKA], ['us', HAWAII],
];

/** Whether (lon, lat) is inside `ring`: the crossing-number test. */
function inside(ring: Ring, lon: number, lat: number): boolean {
  let hit = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/** The country at (lat, lon), or undefined where none of these is. */
export function countryAt(lat: number, lon: number): Country | undefined {
  for (const [country, ring] of OUTLINES) if (inside(ring, lon, lat)) return country;
  return undefined;
}
